import Promise from 'bluebird';
import _ from 'underscore';
import { useContext } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import {
  loadCampaign,
  loadBundleCountries,
  modelToViewModelForCampaign,
  getErrorsPlaceholders,
  saveCampaign,
} from '@bingads-webui-campaign-react/xandr-campaign';
import { getDealDetails } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import exclusionRepository from '@bingads-webui-campaign-legacy/advanced-settings/util/exclusion-repository';
import { ScopeLevel } from '@bingads-webui-campaign-react/constants';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ActivityContext, ScenarioContext } from '@bingads-webui-react/hoc-with-signal';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { getVerifiedTrackingSettingWrite as viewModelToBulkModelForVerifiedTrackingSettings } from '@bingads-webui-campaign-react/third-party-measurement';
import { viewModelToBulkModelForDisclaimerAssociation } from '@bingads-webui-campaign-react/ads-campaign-disclaimers';
import { showBudgetBidCampaignNotifications } from '@bingads-webui-campaign/import-shared-utils';
import { repository } from '@bingads-webui-campaign-react/website-list-selector';
import { isCTVCampaign, isDealCampaign } from '../../step-constants';
import {
  readDayTimeCriterions,
  viewModelToBulkModelForAdSchedules,
  readLocationCriterions,
  readDeviceCriterion,
} from '../../shared/model/campaign-criterion';

export const useCampaignEditRepository = () => {
  const i18n = useContext(I18nContext);
  const { state, send } = useWizardService();

  const { scope } = useContext(ScopeContext) || {};
  const scenarioContext = useContext(ScenarioContext);
  const serverContext = useContext(ServerContext);
  const { DynamicPermissions = {} } = serverContext || {};
  const {
    AudienceMMACTAOptOut: isAudienceMMACTAOptOutEnabled,
    AudienceMMACTADefaultOptOut: isAudienceMMACTADefaultOptOutEnabled,
    WebsiteExclusionLists: isWebsiteExclusionListsEnabled,
  } = DynamicPermissions;

  const activity = useContext(ActivityContext);
  const instrumentation = useContext(InstrumentationContext);

  const { cid, aid } = scope.values;
  const { flow } = state.context.wizardData;
  const isCTVCampaignFlow = isCTVCampaign(flow);
  const isDealCampaignFlow = isDealCampaign(flow);

  const load = () => {
    const savedCampaign = state.context.wizardData.campaign;
    const campaignId = savedCampaign ? savedCampaign.Id : null;

    if (isCTVCampaignFlow || isDealCampaignFlow) {
      const loadPromises = {
        campaign: loadCampaign(cid, aid, campaignId),
        dealDetails: getDealDetails(cid, aid, campaignId, serverContext),
      };

      return Promise.props(loadPromises).then(({
        campaign,
        dealDetails,
      }) => {
        const campaignData = modelToViewModelForCampaign({
          campaignModel: campaign,
          dealDetails,
        });

        return {
          campaign: campaignData.data,
          dealDetails,
          ...getErrorsPlaceholders(),
        };
      });
    }

    const loadPromises = {
      campaign: loadCampaign(cid, aid, campaignId),
      exclusions: exclusionRepository.load(campaignId, null, ScopeLevel.Campaign),
      dayTimeCriterions: readDayTimeCriterions({
        cid, aid, campaignId,
      }),
      locationCriterions: readLocationCriterions({
        cid, aid, campaignId,
      }),
      campaignDeviceCriterions: readDeviceCriterion({
        cid, aid, campaignId,
      }),
      bundleCountriesAndLocationCriterions: loadBundleCountries({
        cid,
        aid,
        serverContext,
      }),
    };

    if (isWebsiteExclusionListsEnabled) {
      loadPromises.websiteExclusionLists = repository.getListsByCampaignId(serverContext, campaignId);
    } else {
      loadPromises.websiteExclusionLists = Promise.resolve([]);
    }

    return Promise.props(loadPromises).then(({
      campaign,
      exclusions: [campaignExclusions],
      dayTimeCriterions,
      locationCriterions,
      campaignDeviceCriterions,
      bundleCountriesAndLocationCriterions,
      websiteExclusionLists,
    }) => {
      const campaignData = modelToViewModelForCampaign({
        campaignModel: campaign,
        dayTimeCriterionsModel: dayTimeCriterions ? dayTimeCriterions.value : [],
        locationCriterionsModel: locationCriterions ? locationCriterions.value : [],
        campaignDeviceCriterions: campaignDeviceCriterions ? campaignDeviceCriterions.value : [],
        bundleCountries: bundleCountriesAndLocationCriterions.bundleCountries,
        isAudienceMMACTAOptOutEnabled,
        isAudienceMMACTADefaultOptOutEnabled,
      });

      return {
        campaign: campaignData.data,
        ...bundleCountriesAndLocationCriterions,
        exclusions: {
          campaignExclusions,
        },
        websiteExclusionLists: websiteExclusionLists.map(entity => ({ ...entity, value: entity.Id, content: entity.Name })),
        ...getErrorsPlaceholders(),
      };
    });
  };

  const allSettledPromiseWrapper = (promises) => {
    const wrappedPromises = promises.map(p => Promise.resolve(p)
      .then(
        val => ({ status: 'fulfilled', value: val }),
        err => ({ status: 'rejected', reason: err })
      ));
    return Promise.all(wrappedPromises);
  };

  const save = ({ data }) => {
    const {
      campaign,
      bundleCountries,
      bundleLocationCriterions,
      exclusions,
      websiteExclusionLists,
    } = data;

    if (campaign.Status === 'UserPaused') {
      campaign.Status = 'Paused';
    }

    let saveCampaignPromise;
    let saveExclusionsPromise = Promise.resolve();
    let saveWebsiteExclusionListsPromise = Promise.resolve();

    if (isCTVCampaignFlow || isDealCampaignFlow) {
      saveCampaignPromise = saveCampaign({
        campaign: { data: campaign },
        viewModelToBulkModelForVerifiedTrackingSettings,
      }, {
        scenario: scenarioContext.name,
        activityLogger: scenarioContext.createActivity('saveDefaultCampaign'),
        i18n,
      });
    } else {
      saveCampaignPromise = saveCampaign({
        campaign: { data: campaign },
        viewModelToBulkModelForAdSchedules,
        viewModelToBulkModelForVerifiedTrackingSettings,
        bundleCountries,
        bundleLocationCriterions,
        viewModelToBulkModelForDisclaimerAssociation,
        isAudienceMMACTAOptOutEnabled,
      }, {
        scenario: scenarioContext.name,
        activityLogger: scenarioContext.createActivity('saveDefaultCampaign'),
        i18n,
      });

      const saveExclusionsData = {
        campaignId: campaign.Id,
        AdvanceSettings: {
          Exclusions: {
            ExcludedIPs: exclusions.campaignExclusions.ExcludedIPs,
            ExcludedSites: exclusions.campaignExclusions.ExcludedSites,
          },
        },
      };
      saveExclusionsPromise = exclusionRepository.saveExtensions(saveExclusionsData, ScopeLevel.Campaign);
      if (isWebsiteExclusionListsEnabled) {
        saveWebsiteExclusionListsPromise = repository.associateListsToCampaign(serverContext, websiteExclusionLists, campaign.Id);
      }
    }

    const savePromises = [saveCampaignPromise, saveExclusionsPromise, saveWebsiteExclusionListsPromise];

    return new Promise((resolve, reject) => {
      allSettledPromiseWrapper(savePromises).then((results) => {
        const allSuccess = _.every(results, result => result.status === 'fulfilled');

        if (allSuccess) {
          const { activity: BudgetBidCampaignImportNotificationActivity } = instrumentation.createScenarioAndActivity({
            scenarioName: 'BudgetBidCampaignImportNotification',
            activityName: 'AudienceCampaignSettingPage',
          });

          const { campaign: initialCampaign } = state.context.stepContexts.CAMPAIGN_EDIT.initialData;

          showBudgetBidCampaignNotifications({
            campaignItemList: [initialCampaign],
            oldBudget: initialCampaign.Budget,
            newBudget: campaign.Budget,
            oldBidStrategy: initialCampaign.BiddingScheme,
            newBidStrategy: campaign.BiddingScheme,
            activity: BudgetBidCampaignImportNotificationActivity,
          });

          if (activity) {
            activity.signal(`onBudgetAndBidSavedSuccessfully - Status: ${campaign.Status}, Id: ${campaign.Id}`);
          }

          if (isCTVCampaignFlow || isDealCampaignFlow) {
            send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { campaign: results[0].value.campaign.data });
          } else {
            send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { campaign: results[0].value.campaign.data, exclusions: results[1].value });
          }

          return resolve({
            campaign: results[0].value.campaign.data,
            ...getErrorsPlaceholders(),
          });
        }

        const errors = _.map(results, (result) => {
          if (activity) {
            activity.signal(`OnBudgetAndBidSavedError - error: ${JSON.stringify(result)}`);
          }
          if (result.status === 'fulfilled') {
            return null;
          }

          const { serverErrors = {} } = result.reason;

          if (result.reason.NegativeSitesResult) {
            const { NegativeSitesResult } = result.reason;
            serverErrors.property = 'webExclusions';
            serverErrors.NegativeSitesResult = NegativeSitesResult.EntityErrors;
          }

          if (result.reason.NegativeIPsResult) {
            const { NegativeIPsResult } = result.reason;
            serverErrors.property = 'webExclusions';
            serverErrors.NegativeIPsResult = NegativeIPsResult.EntityErrors;
          }

          return serverErrors;
        });

        // eslint-disable-next-line prefer-promise-reject-errors
        return reject({ error: _.flatten(errors.filter(val => val !== null)) });
      });
    });
  };

  return {
    load,
    save,
  };
};
